import {useMemo, useState} from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {Input} from '@common-web/components/ui/input';
import {Search} from 'lucide-react';
import {ExtractedColumn} from '../types';
import {FileIdBadge} from '@client/pages/payroll/components/FileIdBadge';

export function ColumnSelect({
  columns,
  value,
  onValueChange,
  placeholder = 'Select column',
  className = '',
}: {
  columns: ExtractedColumn[];
  value: string | undefined;
  onValueChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}) {
  const [searchText, setSearchText] = useState('');

  const columnsByFile = useMemo(() => {
    const grouped: Record<string, ExtractedColumn[]> = {};
    const files: string[] = [];

    for (const col of columns) {
      const fileId = col.fileId || 'default';
      if (!grouped[fileId]) {
        grouped[fileId] = [];
        files.push(fileId);
      }
      grouped[fileId].push(col);
    }

    return {grouped, files};
  }, [columns]);

  const matchingColumns = useMemo(() => {
    const result: {fileId: string; columns: ExtractedColumn[]}[] = [];

    for (const fileId of columnsByFile.files) {
      const fileColumns = columnsByFile.grouped[fileId]
        .sort((a, b) => a.name.localeCompare(b.name))
        .filter((c) =>
          c.name
            .replace(/\s/g, '')
            .toLowerCase()
            .includes(searchText.replace(/\s/g, '').toLowerCase()),
        );

      if (fileColumns.length > 0) {
        result.push({fileId, columns: fileColumns});
      }

      if (value) {
        const isComposite = value.includes(':');
        const [selectedFileId, selectedIdx] = isComposite ? value.split(':') : [fileId, value];
        if (selectedFileId === fileId) {
          const selectedColumn = columns.find((c) => {
            if (isComposite) {
              return c.fileId === selectedFileId && String(c.index) === selectedIdx;
            }
            return String(c.index) === selectedIdx;
          });
          if (selectedColumn && !fileColumns.includes(selectedColumn)) {
            const fileResult = result.find((r) => r.fileId === fileId);
            if (fileResult) {
              fileResult.columns.unshift(selectedColumn);
            } else {
              result.push({fileId, columns: [selectedColumn]});
            }
          }
        }
      }
    }

    return result;
  }, [columnsByFile, searchText, value, columns]);

  const handleValueChange = (val: string) => {
    onValueChange(val);
    setSearchText('');
  };

  return (
    <Select value={value ?? ''} onValueChange={handleValueChange}>
      <SelectTrigger className={className}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className='max-h-[400px]'>
        <div className='p-2 border-b sticky top-0 bg-white z-10'>
          <div className='relative'>
            <Search className='absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-internal-brand-gray' />
            <Input
              placeholder='Search columns...'
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className='pl-8 h-8 border-gray-200 focus:border-internal-brand-blue placeholder:text-gray-400 focus:ring-internal-brand-blue'
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
        </div>
        <div className='max-h-[300px] overflow-y-auto'>
          {matchingColumns.length === 0 ? (
            <div className='p-4 text-center text-sm text-internal-brand-gray'>
              No columns found matching "{searchText}"
            </div>
          ) : (
            matchingColumns.map((fileGroup) => (
              <div key={fileGroup.fileId}>
                <div className='px-3 py-2 bg-gray-50 border-b border-gray-100 sticky top-0 z-20'>
                  <div className='flex items-center gap-2 text-xs font-semibold text-internal-brand-navy'>
                    <FileIdBadge fileId={fileGroup.fileId} />
                    <span className='ml-auto text-internal-brand-gray'>
                      {fileGroup.columns.length} column{fileGroup.columns.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
                {fileGroup.columns.map((columnOption) => {
                  const hasFile = !!columnOption.fileId;
                  const compositeValue = hasFile
                    ? `${columnOption.fileId}:${String(columnOption.index)}`
                    : String(columnOption.index);
                  return (
                    <SelectItem
                      key={compositeValue}
                      value={compositeValue}
                      className={'even:bg-gray-50/50 even:border-y even:border-gray-200'}
                    >
                      <div className='flex flex-col items-start max-w-[400px] min-h-[40px]'>
                        <span
                          className='font-medium text-sm text-gray-700'
                          title={columnOption.name}
                        >
                          {columnOption.name}
                        </span>
                        {columnOption.samples?.[0] && (
                          <span
                            className='text-xs text-gray-600 truncate max-w-[400px]'
                            title={columnOption.samples[0]}
                          >
                            e.g. {columnOption.samples[0]}
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </SelectContent>
    </Select>
  );
}
